import { KLAYO_COLORS } from '@/themes';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { ConfirmationDialog } from 'klayowebshared';
import React, { useMemo, useState } from 'react';
import { SettingsEmployee } from '../data/settings/SettingsEmployee';
import MoreVertIcon from '../resources/images/icons-more-vertical.svg';
import { Button } from './Button';
import AnimatedFooter from './dialogs/AnimatedFooter';

interface BulkEmployeeActionBarProps {
  translate: (key: string, params?: Record<string, unknown>) => string;
  employees: SettingsEmployee[];
  selectedRowIds: GridRowSelectionModel;
  onBulkActivate: (targets: SettingsEmployee[]) => void;
  onBulkDeactivate: (targets: SettingsEmployee[]) => void;
  onBulkUpgradeToFull: (targets: SettingsEmployee[]) => Promise<void>;
  onBulkDowngradeToLimited: (targets: SettingsEmployee[]) => Promise<void>;
  onBulkResendInvite: (targets: SettingsEmployee[]) => void;
  theme: any;
}

export function BulkEmployeeActionBar({
  translate,
  employees,
  selectedRowIds,
  onBulkActivate,
  onBulkDeactivate,
  onBulkUpgradeToFull,
  onBulkDowngradeToLimited,
  onBulkResendInvite,
  theme,
}: BulkEmployeeActionBarProps) {
  const muiTheme = useTheme();
  const isAboveSm = useMediaQuery(muiTheme.breakpoints.up('sm'));
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<'upgrade' | 'downgrade' | null>(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const selectedEmployees = useMemo<SettingsEmployee[]>(() => {
    if (!employees?.length || !selectedRowIds.length) return [];
    return employees.filter((emp) =>
      (selectedRowIds as (string | number)[]).some((id) =>
        String(id).endsWith('_' + emp.employeeId)
      )
    );
  }, [employees, selectedRowIds]);

  const activatable = useMemo(
    () => selectedEmployees.filter((e) => e.status === SettingsEmployee.status.inactive),
    [selectedEmployees]
  );

  const deactivatable = useMemo(
    () =>
      selectedEmployees.filter(
        (e) =>
          e.status !== null &&
          [
            SettingsEmployee.status.active,
            SettingsEmployee.status.invited,
            SettingsEmployee.status.added,
          ].includes(e.status)
      ),
    [selectedEmployees]
  );

  const upgradable = useMemo(
    () => selectedEmployees.filter((e) => !e.isFullAccess),
    [selectedEmployees]
  );

  const downgradable = useMemo(
    () => selectedEmployees.filter((e) => e.isFullAccess),
    [selectedEmployees]
  );

  const invitable = useMemo(
    () =>
      selectedEmployees.filter(
        (e) =>
          e.status !== null &&
          [SettingsEmployee.status.added, SettingsEmployee.status.invited].includes(e.status)
      ),
    [selectedEmployees]
  );

  const handleMenuClose = () => setMenuAnchor(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(e.currentTarget);

  if (!isAboveSm) return null;

  return (
    <>
      <AnimatedFooter
        show={selectedRowIds.length > 0}
        sx={{ border: '1px solid', borderColor: KLAYO_COLORS.NeonBlueNB200, minWidth: 'auto' }}
        borderRadius={24}
        padding='12px 24px'
      >
        <Stack
          direction='row'
          alignItems='center'
          sx={{
            gap: 10,
            whiteSpace: 'nowrap',
          }}
        >
          <Typography variant='body2' fontWeight={500} flex={1} color={KLAYO_COLORS.GreyGR900}>
            {translate('settingsViews.employees.selectedCount', { count: selectedRowIds.length })}
          </Typography>
          <Stack direction='row' alignItems='center'>
            {invitable.length > 0 && (
              <Button
                onClick={() => onBulkResendInvite(invitable)}
                variant='text'
                label={
                  <Typography variant='body2' fontWeight={500} color={KLAYO_COLORS.NeonBlueNB500}>
                    {translate('settingsViews.employees.sendInvite')}
                  </Typography>
                }
                size='sm'
              />
            )}
            <IconButton size='small' onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
          </Stack>
        </Stack>
      </AnimatedFooter>

      <Menu
        open={menuAnchor !== null}
        anchorEl={menuAnchor}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={handleMenuClose}
      >
        <MenuItem
          disabled={activatable.length === 0}
          onClick={() => {
            handleMenuClose();
            onBulkActivate(activatable);
          }}
        >
          <ListItemText>
            {translate('settingsViews.employees.bulkActivate', { count: activatable.length })}
          </ListItemText>
        </MenuItem>
        <MenuItem
          disabled={deactivatable.length === 0}
          onClick={() => {
            handleMenuClose();
            onBulkDeactivate(deactivatable);
          }}
        >
          <ListItemText>
            {translate('settingsViews.employees.bulkDeactivate', { count: deactivatable.length })}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={upgradable.length === 0}
          onClick={() => {
            handleMenuClose();
            setConfirmDialog('upgrade');
          }}
        >
          <ListItemText>
            {translate('settingsViews.employees.bulkUpgradeToFull', { count: upgradable.length })}
          </ListItemText>
        </MenuItem>
        <MenuItem
          disabled={downgradable.length === 0}
          onClick={() => {
            handleMenuClose();
            setConfirmDialog('downgrade');
          }}
        >
          <ListItemText>
            {translate('settingsViews.employees.bulkDowngradeToLimited', {
              count: downgradable.length,
            })}
          </ListItemText>
        </MenuItem>
      </Menu>

      {confirmDialog !== null && (
        <ConfirmationDialog
          theme={theme}
          title={translate(
            confirmDialog === 'upgrade'
              ? 'settingsViews.employees.bulkUpgradeConfirmTitle'
              : 'settingsViews.employees.bulkDowngradeConfirmTitle'
          )}
          question={translate(
            confirmDialog === 'upgrade'
              ? 'settingsViews.employees.bulkUpgradeConfirmBody'
              : 'settingsViews.employees.bulkDowngradeConfirmBody'
          )}
          acceptButton={translate(
            confirmDialog === 'upgrade'
              ? 'settingsViews.employees.upgradeToFull'
              : 'settingsViews.employees.downgradeToLimited'
          )}
          cancelButton={translate('common.cancel')}
          loading={isConfirmLoading}
          onAccept={async () => {
            setIsConfirmLoading(true);
            try {
              if (confirmDialog === 'upgrade') {
                await onBulkUpgradeToFull(upgradable);
              } else {
                await onBulkDowngradeToLimited(downgradable);
              }
            } finally {
              setIsConfirmLoading(false);
              setConfirmDialog(null);
            }
          }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </>
  );
}
